import React from "react";
import { useForm } from "react-hook-form";
import { Button } from "primereact/button";
import { PickNullable } from "@interfaces";
import { useNavigate } from "react-router";
import { DialogProps } from "primereact/dialog";
import { Form, Dialog, Dropdown, InputText, MultiSelect } from "@components";

import { IAccountCatalogCreate } from "../../interfaces";
import { useAccountingQuery, useAccountingMutation } from "../../hooks";
import {
  GeneralAccountingRoutes,
  GeneralAccountingRouteNames,
} from "../../routes";

type FormValues = PickNullable<
  IAccountCatalogCreate,
  | "Nombre"
  | "EstadoId"
  | "CatalogoId"
  | "SucursalesIds"
  | "CatalogoTipoId"
  | "SucursalesSelect"
  | "CatalogosCuentasDetalles"
> & { Sucursales: ISucursales[] };

const model = {
  from: (data: IAccountCatalogCreate): FormValues => {
    return {
      Nombre: data.Nombre,
      EstadoId: data.EstadoId,
      CatalogoId: data.CatalogoId,
      Sucursales: data.Sucursales ?? [],
      SucursalesIds: data.SucursalesIds,
      CatalogoTipoId: data.CatalogoTipoId ?? 0,
    };
  },
  to: (data: FormValues): IAccountCatalogCreate => {
    // console.log(data, "data to update");
    return {
      EstadoId: 1,
      Nombre: data.Nombre || "",
      CatalogoId: data.CatalogoId ?? 0,
      Sucursales: data.Sucursales ?? [],
      CatalogoTipoId: data.CatalogoTipoId ?? 0,
      SucursalesIds: data.Sucursales?.map((city) => city.SucursalId) || [],
    };
  },
};

interface ICatalogoType {
  Nombre: string;
  CatalogoTipoId: number;
}

interface ISucursales {
  SucursalId: number;
  SucursalNombre: string;
}

export interface IAccountCatalogDialogProps extends DialogProps {
  cuentaId: number;
}

const AccountCatalogDialog = ({
  cuentaId,
  ...props
}: IAccountCatalogDialogProps) => {
  const navigate = useNavigate();

  const createCatalogo = useAccountingMutation("AccountCatalog", "create");
  const updateCatalogo = useAccountingMutation("AccountCatalog", "update");
  const getCreateAccountCatalog = useAccountingQuery(
    "AccountCatalog",
    "getCreate",
  );

  const { data: accountCatalogData, isFetching: isAccountCatalogFetching } =
    useAccountingQuery("AccountCatalog", "getById", cuentaId, {
      enabled: !!cuentaId,
    });

  const form = useForm<FormValues>({
    defaultValues: {
      Nombre: "",
      CatalogoTipoId: null,
    },
  });
  const { reset, resetField } = form;

  const catalogTypeOptions = React.useMemo(() => {
    if (!getCreateAccountCatalog.data?.CatalogosTiposSelect?.length) return [];
    return getCreateAccountCatalog.data?.CatalogosTiposSelect.map(
      (catalogoTipo: ICatalogoType) => ({
        label: catalogoTipo.Nombre,
        value: catalogoTipo.CatalogoTipoId,
      }),
    );
  }, [getCreateAccountCatalog.data?.CatalogosTiposSelect]);
  // console.log(catalogTypeOptions, "catalogTypeOptions");

  const sucursalOptions = React.useMemo(() => {
    if (!getCreateAccountCatalog.data?.SucursalesSelect?.length) return [];
    return getCreateAccountCatalog.data?.SucursalesSelect.map(
      (sucursal: ISucursales) => ({
        SucursalId: sucursal.SucursalId,
        SucursalNombre: sucursal.SucursalNombre,
      }),
    );
  }, [getCreateAccountCatalog.data?.SucursalesSelect]);

  React.useEffect(() => {
    if (cuentaId && accountCatalogData) {
      // Filtrar las sucursales seleccionadas basadas en SucursalesIds
      const selected = sucursalOptions.filter((option: ISucursales) =>
        accountCatalogData.SucursalesIds?.includes(option.SucursalId),
      );

      const formData = model.from({
        ...accountCatalogData,
        Sucursales: selected,
      });

      reset(formData, { keepDefaultValues: true });
    }
    if (!cuentaId) {
      reset();
      resetField("Sucursales");
    }
  }, [accountCatalogData, cuentaId, reset, resetField, sucursalOptions]);

  const onSubmit = (values: FormValues) => {
    if (cuentaId !== 0) {
      return updateCatalogo.mutateAsync(model.to(values), {
        onSuccess: () => {
          resetField("Sucursales");
          reset();
          props.onHide();
        },
      });
    }

    return createCatalogo.mutateAsync(model.to(values), {
      onSuccess: (result) => {
        if (values.CatalogoTipoId === 1) {
          navigate(
            `.${GeneralAccountingRoutes[
              GeneralAccountingRouteNames.ACCOUNT_CATALOG_SIMPLE_PAGE
            ].create({ id: `${result.CatalogoId}` })}`,
          );
        } else {
          navigate(
            `.${GeneralAccountingRoutes[
              GeneralAccountingRouteNames.ACCOUNT_CATALOG_CONSOLIDATE_PAGE
            ].create({ id: `${result.CatalogoId}` })}`,
          );
        }
        resetField("Sucursales");
        reset();
        props.onHide();
      },
    });
  };

  return (
    <Dialog
      {...props}
      className="w-full md:w-10 lg:w-6"
      subtitle="Rellena los datos del formulario"
      onHide={() => {
        form.resetField("Sucursales");
        form.reset();
        props.onHide();
      }}
      title={
        cuentaId === 0
          ? "Crear catalogo de cuenta"
          : "Editar catalogo de cuenta"
      }
      footer={
        <div className="flex flex-1 justify-content-end gap-2">
          <Button
            size="small"
            severity="warning"
            label="Reiniciar formulario"
            disabled={!form.formState.isDirty}
            onClick={() => {
              form.reset();
            }}
          />
          <Button
            size="small"
            loading={isAccountCatalogFetching}
            onClick={form.handleSubmit(onSubmit)}
            label={cuentaId ? "Guardar cambios" : "Crear catalogo de cuenta"}
          />
        </div>
      }
    >
      <Form form={form}>
        <InputText
          name="Nombre"
          label="Nombre"
          placeholder="Nombre"
          className="p-inputtext-sm"
          rules={{ required: "Este campo es necesario" }}
        />
        <Dropdown
          label="Tipo"
          name="CatalogoTipoId"
          className="p-inputtext-sm"
          options={catalogTypeOptions}
          disabledInput={cuentaId !== 0}
          placeholder="Selecciona un tipo"
          rules={{ required: "Este campo es necesario" }}
        />
        <MultiSelect
          name="Sucursales"
          label="Sucursales"
          options={sucursalOptions}
          className="p-inputtext-sm"
          optionLabel="SucursalNombre"
          placeholder="Seleccione al menos una sucursal"
          rules={{ required: "Este campo es necesario" }}
        />
      </Form>
    </Dialog>
  );
};

export default AccountCatalogDialog;
